'use client';

import React, { useEffect, useState, useLayoutEffect } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Image from 'next/image';
import { DEFAULT_META_TITLE } from '../common/SeoHead';
import { motion, AnimatePresence } from 'framer-motion';
import ImageSlider from '@/components/packages/ImageSlider';
import { useSearchStore } from '@/store/searchStore';
import { Sidebar } from '@/components/navbar/web/Sidebar';
import { MobileNavbar } from '@/components/navbar/mobile/MobileNavbar';
import { ThemeProvider } from '@/design-system/themes';
import { backgroundColors, typographyColors } from '@/design-system/utils';
import { Breadcrumb } from '@/components/common/Breadcrumb';
import BottomNavButton from '@/components/packages/BottomNavButton';
import { ExperienceType } from './types';
import { getExperienceConfig } from './config';
import { FloatingBookingForm } from './FloatingBookingForm';
import { cacheImages } from '@/services/imageCacheService';
import { ExperienceHeader } from './sections/ExperienceHeader';
import { ExperienceDescription } from './sections/ExperienceDescription';
import { ExperienceHighlights } from './sections/ExperienceHighlights';
import { ExperienceItinerary } from './sections/ExperienceItinerary';
import { ExperienceInclusions } from './sections/ExperienceInclusions';
import { ExperienceExclusions } from './sections/ExperienceExclusions';
import { ExperienceWhatToExpect } from './sections/ExperienceWhatToExpect';
import { ExperienceFAQ } from './sections/ExperienceFAQ';
import { ExperienceGuide } from './sections/ExperienceGuide';
import { ExperienceRelated } from './sections/ExperienceRelated';
import { ExperienceAmenities } from './sections/ExperienceAmenities';
import { ExperienceLocation } from './sections/ExperienceLocation';
import { ExperiencePolicies } from './sections/ExperiencePolicies';
import { ExperienceVideosSection } from './sections/ExperienceVideosSection';
import { ExperienceGreatFor } from './sections/ExperienceGreatFor';
import { ExperiencePickupPoint } from './sections/ExperiencePickupPoint';
import { ExperienceActivities } from './sections/ExperienceActivities';
import { ExperienceAccommodationOptions } from './sections/ExperienceAccommodationOptions';
import { ExperienceMap } from './sections/ExperienceMap';
import { ExperienceKeyDetails } from './sections/ExperienceKeyDetails';
import { ExperienceNotices } from './sections/ExperienceNotices';
import { ExperienceVideoGallery } from './sections/ExperienceVideoGallery';
import { ExperienceTargetAudience } from './sections/ExperienceTargetAudience';
import { ExperienceVehicles } from './sections/ExperienceVehicles';
import { ExperienceTransfers } from './sections/ExperienceTransfers';
import { ExperienceHotels } from './sections/ExperienceHotels';
import { ExperienceAirports } from './sections/ExperienceAirports';
import axios from 'axios';
import { getExperienceDetailsByBookingId } from '@/services/bookingsService';
import { getStayExperienceImageUrls } from '@/utils/experienceMapper';
import { Footer } from '@/components/common/Footer';
import {
  getSafeImageUrl,
  resolveFrontendBaseUrl,
  sanitizeDescription,
  sanitizeTitle,
  toAbsoluteUrl,
} from '@/utils/shareMetadata';

interface CommonExperienceDetailPageProps {
  initialData: unknown;
  slug: string;
  type: ExperienceType;
  booking?: unknown;
  bookingId?: string;
}

export default function CommonExperienceDetailPage({
  initialData,
  slug: slugProp,
  type,
  booking,
  bookingId,
}: CommonExperienceDetailPageProps) {
  const setSearchActive = useSearchStore(state => state.setSearchActive);
  const router = useRouter();
  const currentSlug = slugProp || (router.query.slug as string);
  const [slug, setSlug] = useState<string>(currentSlug);
  const [experience, setExperience] = useState<unknown>(() => {
    if (!initialData || booking == null) return initialData;
    return typeof initialData === 'object' && initialData !== null
      ? { ...(initialData as Record<string, unknown>), booking }
      : initialData;
  });
  const [isLoading, setIsLoading] = useState<boolean>(!initialData);
  // Live traveler count from the booking form, used to sync header/bottom-bar
  // pricing with the group vs standard rate the user currently qualifies for
  // (stays, activities, and packages).
  const [stayTravelerCount, setStayTravelerCount] = useState<number | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const config = getExperienceConfig(type);
  const bookingIdInUrl =
    bookingId || (router.query?.booking as string | undefined);
  const hideVehicleAccommodationPickup = !!bookingIdInUrl;

  // When viewing package with ?booking=, hide Vehicle Options, Accommodation, Pickup and Dropoff
  const effectiveSectionOrder =
    type === 'package' && hideVehicleAccommodationPickup
      ? config.sectionOrder.filter(
          key =>
            key !== 'vehicles' &&
            key !== 'accommodationOptions' &&
            key !== 'airports'
        )
      : config.sectionOrder;

  // Merge booking into experience when provided from server (e.g. ?booking= in URL)
  useEffect(() => {
    if (!initialData) return;
    if (
      booking != null &&
      typeof initialData === 'object' &&
      initialData !== null
    ) {
      setExperience(prev => ({
        ...(typeof prev === 'object' && prev !== null
          ? (prev as Record<string, unknown>)
          : {}),
        ...(initialData as Record<string, unknown>),
        booking,
      }));
    } else {
      setExperience(initialData);
    }
  }, [initialData, booking]);

  const isDetailAsPath =
    typeof router.asPath === 'string' &&
    /\/(package-details|activity-details|stay-experience-details|riders-corner)(\/|\?|$)/.test(
      router.asPath
    );

  // Update slug when route changes within the same detail page (client-side)
  useEffect(() => {
    const stillOnDetailRoute =
      typeof router.pathname === 'string' &&
      (router.pathname.includes('package-details') ||
        router.pathname.includes('activity-details') ||
        router.pathname.includes('stay-experience-details') ||
        router.pathname.includes('riders-corner'));

    // Ignore asPath changes from navigations that are leaving this page —
    // those should unmount this component rather than trigger a slug reload.
    if (!stillOnDetailRoute || !isDetailAsPath) return;

    const newSlug = slugProp || (router.query.slug as string);
    if (newSlug && newSlug !== slug) {
      setIsNavigating(true);
      setSlug(newSlug);
      setExperience(null); // Clear old data when slug changes
      setIsLoading(true);
      // Reset navigating state after animation
      const timeoutId = setTimeout(() => setIsNavigating(false), 500);
      return () => clearTimeout(timeoutId);
    }
  }, [router.query.slug, router.pathname, slugProp, slug, isDetailAsPath]);

  // Clear body locks / navigating overlay if we leave or unmount mid-transition
  useEffect(() => {
    const cleanupBodyLocks = () => {
      document.body.classList.remove('overflow-hidden');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.pointerEvents = '';
      document.body.removeAttribute('data-scroll-locked');
    };

    const handleRouteChangeStart = () => {
      setIsNavigating(false);
      cleanupBodyLocks();
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', cleanupBodyLocks);
    router.events.on('routeChangeError', cleanupBodyLocks);

    return () => {
      cleanupBodyLocks();
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', cleanupBodyLocks);
      router.events.off('routeChangeError', cleanupBodyLocks);
    };
  }, [router.events]);

  // Ensure detail page starts at top immediately
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(`${type}-scroll-position`);
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [router.asPath, type]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setSearchActive(false);
    sessionStorage.removeItem(`${type}-scroll-position`);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  }, [setSearchActive, router.asPath, type]);

  // Fetch experience data when slug changes (client-side).
  // Wait for router.isReady so router.query.booking is set when URL has ?booking= (avoids fetching without booking param on client-side nav).
  useEffect(() => {
    if (!slug) return;
    if (!isDetailAsPath) return;

    let cancelled = false;

    const fetchExperience = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const bookingIdFromQuery = router.query?.booking as string | undefined;

        // When package + booking ID: use GET /bookings/v1/:bookingId/experience-details for latest experience, then slug endpoint for booking
        if (
          type === 'package' &&
          bookingIdFromQuery &&
          typeof bookingIdFromQuery === 'string'
        ) {
          try {
            const experienceRes =
              await getExperienceDetailsByBookingId(bookingIdFromQuery);
            if (cancelled) return;
            if (
              experienceRes.success &&
              experienceRes.data?.bookingType === 'package' &&
              experienceRes.data?.experienceDetails != null
            ) {
              const experienceDetails = experienceRes.data
                .experienceDetails as Record<string, unknown>;
              const detailsUrl =
                config.apiEndpoints.details(slug) +
                (config.apiEndpoints.details(slug).includes('?') ? '&' : '?') +
                `booking=${encodeURIComponent(bookingIdFromQuery)}`;
              const slugResponse = await axios.get(detailsUrl);
              if (cancelled) return;
              const bookingFromSlug =
                slugResponse.data.booking ??
                (slugResponse.data.data &&
                typeof slugResponse.data.data === 'object' &&
                'booking' in slugResponse.data.data
                  ? (slugResponse.data.data as { booking?: unknown }).booking
                  : undefined);
              setExperience(
                typeof experienceDetails === 'object' &&
                  experienceDetails !== null
                  ? {
                      ...experienceDetails,
                      ...(bookingFromSlug != null && {
                        booking: bookingFromSlug,
                      }),
                    }
                  : slugResponse.data.data
              );
              return;
            }
          } catch {
            // Fall through to slug-only fetch
          }
        }

        let url = config.apiEndpoints.details(slug);
        if (bookingIdFromQuery) {
          url += url.includes('?') ? '&' : '?';
          url += `booking=${encodeURIComponent(bookingIdFromQuery)}`;
        }
        const response = await axios.get(url);
        if (cancelled) return;
        const data = response.data.data;
        const bookingFromResponse = response.data.booking ?? data?.booking;
        setExperience(
          bookingFromResponse != null &&
            typeof data === 'object' &&
            data !== null
            ? { ...data, booking: bookingFromResponse }
            : data
        );
      } catch (err) {
        if (cancelled) return;
        setError('Failed to load experience.');
        console.error('Error fetching experience:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    // Only use initialData on the very first render if it matches the current slug
    // For client-side navigation, always fetch new data
    const isInitialLoad = initialData && slug === currentSlug;

    if (isInitialLoad && !experience) {
      setExperience(initialData);
      setIsLoading(false);
      return;
    }

    // When URL has ?booking=, wait for router to be ready so we fetch with booking param (latest booking modification)
    if (!router.isReady && typeof window !== 'undefined') {
      const asPathHasBooking = /[?&]booking=/.test(router.asPath);
      if (asPathHasBooking) {
        return; // Defer fetch until router.isReady so router.query.booking is set
      }
    }

    if (!isInitialLoad || experience === null) {
      fetchExperience();
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only re-run when slug or router (booking) changes; avoid deps on initialData/experience to prevent double fetch
  }, [slug, router.isReady, router.query?.booking, router.asPath, isDetailAsPath]);

  const handleNewChat = () => {
    router.push('/chat?new=1');
  };

  const handleLogoClick = () => {
    router.push('/chat');
  };

  // Extract pricing info based on type
  const getPricingInfo = () => {
    if (!experience) return { amountValue: 0, pricePer: '', currency: '' };

    if (type === 'stay') {
      const stayExp = experience as {
        pricing?: {
          pricePerPerson?: number;
          currency?: string;
          groupPricing?: Array<{
            isDisplayPrice?: boolean;
            minHeadCount?: number;
            maxHeadCount?: number;
            adultPrice?: number;
          }>;
        };
      };
      const currency = stayExp?.pricing?.currency || '';

      // When the user has picked a traveler count in the booking form, reflect
      // the tier that matches it (group when in range, else standard rate).
      if (typeof stayTravelerCount === 'number' && stayTravelerCount > 0) {
        const matchedGroup = stayExp?.pricing?.groupPricing?.find(
          g =>
            typeof g.minHeadCount === 'number' &&
            typeof g.maxHeadCount === 'number' &&
            stayTravelerCount >= g.minHeadCount &&
            stayTravelerCount <= g.maxHeadCount
        );
        return {
          amountValue:
            matchedGroup?.adultPrice ?? stayExp?.pricing?.pricePerPerson ?? 0,
          pricePer: 'person/night',
          currency,
        };
      }

      const displayGroup = stayExp?.pricing?.groupPricing?.find(
        g => g.isDisplayPrice
      );
      return displayGroup
        ? {
            amountValue: displayGroup.adultPrice ?? 0,
            pricePer: 'person/night',
            currency,
          }
        : {
            amountValue: stayExp?.pricing?.pricePerPerson ?? 0,
            pricePer: 'person/night',
            currency,
          };
    }

    if (type === 'package') {
      const pkg = experience as {
        price?: number;
        pricePer?: string;
        currency?: string;
        useGroupPricing?: boolean;
        groupBooking?: Array<{
          isDisplayPrice?: boolean;
          minHeadCount?: number;
          maxHeadCount?: number;
          adultPrice?: number;
        }>;
      };
      const currency = pkg?.currency || '';

      // When the user has picked a traveler count in the booking form, reflect
      // the tier that matches it (group when in range, else standard rate).
      if (typeof stayTravelerCount === 'number' && stayTravelerCount > 0) {
        const matchedGroup = pkg?.groupBooking?.find(
          g =>
            typeof g.minHeadCount === 'number' &&
            typeof g.maxHeadCount === 'number' &&
            stayTravelerCount >= g.minHeadCount &&
            stayTravelerCount <= g.maxHeadCount
        );
        return {
          amountValue: matchedGroup?.adultPrice ?? pkg?.price ?? 0,
          pricePer: 'person',
          currency,
        };
      }

      const displayGroup = pkg?.useGroupPricing
        ? pkg?.groupBooking?.find(g => g.isDisplayPrice)
        : undefined;
      return displayGroup
        ? {
            amountValue:
              (displayGroup.adultPrice ?? 0) * displayGroup.minHeadCount,
            pricePer: `${displayGroup.minHeadCount}${
              displayGroup.maxHeadCount ? `-${displayGroup.maxHeadCount}` : '+'
            } person${
              (displayGroup.maxHeadCount ?? displayGroup.minHeadCount) > 1
                ? 's'
                : ''
            }`,
            currency,
          }
        : {
            amountValue: pkg?.price ?? 0,
            pricePer: pkg?.pricePer ?? '',
            currency,
          };
    }

    // Activity
    const activity = experience as {
      price?: number;
      pricePer?: string;
      currency?: string;
      useGroupPricing?: boolean;
      groupBooking?: Array<{
        isDisplayPrice?: boolean;
        minHeadCount?: number;
        maxHeadCount?: number;
        adultPrice?: number;
      }>;
    };
    const currency = activity?.currency || '';

    // When the user has picked a traveler count in the booking form, reflect
    // the tier that matches it (group when in range, else standard rate).
    if (typeof stayTravelerCount === 'number' && stayTravelerCount > 0) {
      const matchedGroup = activity?.groupBooking?.find(
        g =>
          typeof g.minHeadCount === 'number' &&
          typeof g.maxHeadCount === 'number' &&
          stayTravelerCount >= g.minHeadCount &&
          stayTravelerCount <= g.maxHeadCount
      );
      return {
        amountValue: matchedGroup?.adultPrice ?? activity?.price ?? 0,
        pricePer: 'person',
        currency,
      };
    }

    const displayGroup = activity?.useGroupPricing
      ? activity?.groupBooking?.find(g => g.isDisplayPrice)
      : undefined;
    return displayGroup
      ? {
          amountValue:
            (displayGroup.adultPrice ?? 0) * displayGroup.minHeadCount,
          pricePer: `${displayGroup.minHeadCount}${
            displayGroup.maxHeadCount ? `-${displayGroup.maxHeadCount}` : '+'
          } person${
            (displayGroup.maxHeadCount ?? displayGroup.minHeadCount) > 1
              ? 's'
              : ''
          }`,
          currency,
        }
      : {
          amountValue: activity?.price ?? 0,
          pricePer: activity?.pricePer ?? '',
          currency,
        };
  };

  const pricingInfo = getPricingInfo();

  // Get images based on type
  const getImages = (): string[] => {
    if (!experience) return [];
    if (type === 'stay') {
      return getStayExperienceImageUrls(experience as Record<string, unknown>);
    }
    const exp = experience as { imageUrl?: string[] };
    return exp?.imageUrl || [];
  };

  const images = getImages();

  // Preload images when page loads
  useEffect(() => {
    if (images.length > 0) {
      // Preload first 5 images immediately for faster initial load
      const imagesToPreload = images.slice(0, 5);
      cacheImages(imagesToPreload).catch(() => {
        // Silently fail - caching is non-critical
      });
    }
  }, [images]);

  // Get title based on type
  // Use the same priority as list cards: shortTitle > title > name
  const getTitle = (): string => {
    if (!experience) return '';
    const exp = experience as {
      shortTitle?: string | null;
      title?: string;
      name?: string;
    };

    // Helper to check if a string is non-empty
    const isNonEmpty = (str: string | null | undefined): boolean => {
      return (
        str !== null &&
        str !== undefined &&
        typeof str === 'string' &&
        str.trim().length > 0
      );
    };

    // Priority: shortTitle (if non-empty) > title > name
    if (isNonEmpty(exp.shortTitle)) {
      return exp.shortTitle!.trim();
    }
    if (isNonEmpty(exp.title)) {
      return exp.title!.trim();
    }
    if (isNonEmpty(exp.name)) {
      return exp.name.trim();
    }
    return '';
  };

  const title = getTitle();

  // Get description based on type
  const getDescription = (): string => {
    if (!experience) return '';
    if (type === 'stay') {
      return (
        (experience as { longDescription?: string })?.longDescription || ''
      );
    }
    return (experience as { description?: string })?.description || '';
  };

  const description = getDescription();
  const seoTitle = sanitizeTitle(
    title ? `${title} - ${config.pageTitle} | Nexgo AI` : undefined
  );
  const seoDescription = sanitizeDescription(description);
  const canonicalPath =
    type === 'package'
      ? `/package-details/${slug}`
      : type === 'activity'
        ? `/activity-details/${slug}`
        : type === 'stay'
          ? `/stay-experience-details/${slug}`
          : `/riders-corner/${slug}`;
  const canonicalUrl = toAbsoluteUrl(
    bookingIdInUrl
      ? `${canonicalPath}?booking=${encodeURIComponent(bookingIdInUrl)}`
      : canonicalPath,
    resolveFrontendBaseUrl()
  );
  const firstImageUrl = getSafeImageUrl(images[0]);

  // Render section based on component name
  const renderSection = (sectionKey: string) => {
    const section = config.sections[sectionKey];
    if (!section) return null;

    // Check condition if exists
    if (section.condition && !section.condition(experience)) {
      return null;
    }

    switch (section.component) {
      case 'Header':
        return (
          <ExperienceHeader
            key={sectionKey}
            experience={experience}
            type={type}
            slug={slug}
            travelerCount={stayTravelerCount ?? undefined}
          />
        );
      case 'Description':
        return (
          <ExperienceDescription
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Highlights':
        return (
          <ExperienceHighlights
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Itinerary':
        return (
          <ExperienceItinerary
            key={sectionKey}
            experience={experience}
            type={type}
            showItineraryInfoIcon={
              type === 'package' ? !bookingIdInUrl : undefined
            }
            showBenefits={
              type === 'package' && bookingIdInUrl ? false : undefined
            }
            showHotelAsHighlights={
              type === 'package' && bookingIdInUrl ? true : undefined
            }
            isBookingContext={Boolean(bookingIdInUrl)}
          />
        );
      case 'Inclusions':
        return (
          <ExperienceInclusions
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Exclusions':
        return (
          <ExperienceExclusions
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'WhatToExpect':
        return (
          <ExperienceWhatToExpect
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'FAQ':
        return (
          <ExperienceFAQ key={sectionKey} experience={experience} type={type} />
        );
      case 'Guide':
        return <ExperienceGuide key={sectionKey} experience={experience} />;
      case 'Related':
        return (
          <ExperienceRelated
            key={sectionKey}
            experience={experience}
            type={type}
            config={config}
          />
        );
      case 'Amenities':
        return (
          <ExperienceAmenities
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Location':
        return (
          <ExperienceLocation
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Policies':
        return (
          <ExperiencePolicies
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Videos':
        return (
          <ExperienceVideosSection
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'GreatFor':
        return (
          <ExperienceGreatFor
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'PickupPoint':
        return (
          <ExperiencePickupPoint
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Map':
        return (
          <ExperienceMap key={sectionKey} experience={experience} type={type} />
        );
      case 'KeyDetails':
        return (
          <ExperienceKeyDetails
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Notices':
        return (
          <ExperienceNotices
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'VideoGallery':
        return (
          <ExperienceVideoGallery
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'TargetAudience':
        return (
          <ExperienceTargetAudience
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Vehicles':
        return (
          <ExperienceVehicles
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Transfers':
        return (
          <ExperienceTransfers
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Hotels':
        return (
          <ExperienceHotels
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Airports':
        return (
          <ExperienceAirports
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'Activities':
        return (
          <ExperienceActivities
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      case 'AccommodationOptions':
        return (
          <ExperienceAccommodationOptions
            key={sectionKey}
            experience={experience}
            type={type}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider defaultMode='light'>
      {/* Page Transition Overlay */}
      <AnimatePresence>
        {isNavigating && (
          <motion.div
            className='fixed inset-0 z-[9999] bg-white'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className='flex items-center justify-center h-full'>
              <motion.div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  border: `3px solid ${typographyColors.secondary}`,
                  borderTopColor: typographyColors.primary,
                  borderRightColor: typographyColors.primary,
                }}
                animate={{ rotate: 360 }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        style={{
          minHeight: '100vh',
          backgroundColor: backgroundColors.primary,
          display: 'flex',
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {!bookingIdInUrl && (
          <>
            <Sidebar onNewChat={handleNewChat} onLogoClick={handleLogoClick} />
            <div className='md:hidden'>
              <MobileNavbar
                onNewChat={handleNewChat}
                onLogoClick={handleLogoClick}
              />
            </div>
          </>
        )}

        <main
          className={`flex-1 min-w-0 ${bookingIdInUrl ? 'ml-0 pt-0' : 'ml-0 md:ml-[106px] pt-[56px] md:pt-0'}`}
          style={{ backgroundColor: backgroundColors.primary }}
        >
          <Head>
            <title>{seoTitle || DEFAULT_META_TITLE}</title>
            <meta name='description' content={seoDescription} />
            <meta
              property='og:title'
              content={seoTitle || DEFAULT_META_TITLE}
            />
            <meta property='og:description' content={seoDescription} />
            <meta property='og:type' content='website' />
            {canonicalUrl && <meta property='og:url' content={canonicalUrl} />}
            {firstImageUrl && (
              <meta property='og:image' content={firstImageUrl} />
            )}
            <meta property='og:site_name' content='Nexgo AI' />
            <meta
              name='twitter:card'
              content={firstImageUrl ? 'summary_large_image' : 'summary'}
            />
            <meta
              name='twitter:title'
              content={seoTitle || DEFAULT_META_TITLE}
            />
            <meta name='twitter:description' content={seoDescription} />
            {firstImageUrl && (
              <meta name='twitter:image' content={firstImageUrl} />
            )}
          </Head>
          {bookingIdInUrl ? (
            <div className='flex justify-center w-full pt-4 pb-2'>
              <button
                type='button'
                onClick={() => router.push('/chat?view=full-landing')}
                className='focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded-lg'
                aria-label='Go to home'
              >
                <Image
                  src='/assets/images/logo.svg'
                  alt='Nexgo'
                  width={120}
                  height={36}
                  className='h-8 md:h-9 w-auto'
                />
              </button>
            </div>
          ) : (
            <Breadcrumb
              items={[
                { label: 'Home', href: '/chat?view=full-landing' },
                { label: 'Experiences', href: '/experiences' },
                { label: config.pageTitle },
              ]}
            />
          )}
          <div
            className='min-h-screen w-full flex flex-col py-0 px-4 md:pl-[56px] md:pr-[56px]'
            style={{
              fontSize: '16px',
            }}
          >
            <div>
              <AnimatePresence mode='wait'>
                {isLoading && (
                  <motion.div
                    key='loading'
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className='text-center mb-4'
                  >
                    <div className='flex flex-col items-center justify-center py-8'>
                      <motion.div
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          border: `3px solid ${typographyColors.secondary}`,
                          borderTopColor: typographyColors.primary,
                          borderRightColor: typographyColors.primary,
                        }}
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: 'linear',
                        }}
                      />
                      <p className='text-gray-500 mt-4 text-sm'>
                        Loading experience details...
                      </p>
                    </div>
                  </motion.div>
                )}

                {error && (
                  <motion.p
                    key='error'
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className='text-center text-red-500 mb-4'
                  >
                    {error}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            <AnimatePresence mode='wait'>
              {images.length > 0 && (
                <motion.div
                  key={`images-${slug}`}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                  <ImageSlider images={images} />
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {experience && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <BottomNavButton
                    scrollToId='booking-form'
                    label='Check availability'
                    amount={pricingInfo.amountValue}
                    amountby={pricingInfo.pricePer}
                    backendCurrency={pricingInfo.currency}
                    experience={experience}
                    type={type}
                    onTravelerCountChange={setStayTravelerCount}
                    hideAtTop={true}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <div className='flex flex-col lg:flex-row w-full mt-10 mb-10 gap-x-6'>
                {/* Main Content */}
                <motion.div
                  className='flex-1 space-y-4'
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                >
                  <AnimatePresence mode='wait'>
                    {experience &&
                      effectiveSectionOrder.map((sectionKey, index) => (
                        <motion.div
                          key={`${sectionKey}-${slug}`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{
                            duration: 0.4,
                            delay: index * 0.05,
                            ease: 'easeOut',
                          }}
                        >
                          {renderSection(sectionKey)}
                        </motion.div>
                      ))}
                  </AnimatePresence>
                </motion.div>

                {/* Sticky Booking Form - Desktop */}
                <motion.div
                  className='lg:w-[338px] lg:flex-shrink-0 pt-6 lg:block hidden'
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
                >
                  <AnimatePresence>
                    {experience && (
                      <motion.div
                        key={`booking-${slug}`}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.4 }}
                      >
                        <FloatingBookingForm
                          experience={experience}
                          type={type}
                          onTravelerCountChange={setStayTravelerCount}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>
            </div>

            {/* Mobile Booking Form */}
            <AnimatePresence>
              {experience && (
                <motion.div
                  className='lg:hidden pt-6 pb-20'
                  id='booking-form'
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <FloatingBookingForm
                    experience={experience}
                    type={type}
                    onTravelerCountChange={setStayTravelerCount}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <Footer />
        </main>
      </motion.div>
    </ThemeProvider>
  );
}
